/**
 * Chart primitives for the Admin Portal.
 *
 * Inline SVG, no charting library. Four small forms are all this portal needs,
 * and a dependency would cost more in bundle size and version drift than it
 * saves in code — while giving up the one thing that matters here, which is
 * control over exactly which colour encodes what.
 *
 * ## The colour rules these follow
 *
 * **Plans are ordinal, not categorical.** Free → Basic → Standard → Premium is a
 * size order, so it takes a single-hue ramp stepped light-to-dark rather than
 * four unrelated hues: the reader sees the ordering in the colour instead of
 * having to learn a legend. The four steps below are the brand cyan ramp,
 * checked for monotone lightness, adjacent step separation, and contrast
 * against the card surface.
 *
 * **Subscription states are status, not identity.** Active/trial/cancelled/
 * expired carry reserved meanings, so they use the status palette and are never
 * reused as "series 4". Each one ships with its label beside the swatch, so the
 * meaning never rests on colour alone.
 *
 * **A single series gets no legend.** The chart title names it; a legend box
 * with one entry is furniture.
 *
 * Everything is sized in a `viewBox` and scaled by CSS, so the charts stay
 * legible down to phone width without a resize observer. Each one carries a
 * table view for screen readers and for anyone who needs the numbers rather
 * than the shape.
 */

import { useId, useState, type ReactNode } from 'react';
import { cx } from './ui';

// --- Palette --------------------------------------------------------------

/**
 * Plan ramp, dimmest (Free) to brightest (Premium).
 *
 * Validated as an ordinal ramp against the card surface `#141e33`: lightness is
 * monotone, every adjacent gap clears ΔL 0.06, the dim end holds 2.8:1, and the
 * hue spread is 14° — one hue, four readable steps.
 */
export const PLAN_RAMP = ['#0d6d85', '#12b3cc', '#38cfe3', '#7fe3f0'] as const;

/** Single-series marks. Both clear 3:1 on the card surface by a wide margin. */
export const SERIES_REVENUE = '#38cfe3';
export const SERIES_GROWTH = '#7c8cf8';

/** Recessive: a grid line should be findable, not readable. */
const GRID = '#1e2c47';

/**
 * Axis and label ink.
 *
 * `hull-300` rather than `hull-400`: the lighter step is 7.5:1 on the card,
 * where `hull-400` is 4.1:1 and misses the 4.5:1 floor for text this size.
 */
const AXIS_INK = '#9ab0d0';

export const STATUS_COLORS = {
  active: '#4ade80',
  trialing: '#38cfe3',
  cancelled: '#fbbf24',
  expired: '#f87171',
  neutral: '#6580ab',
} as const;

// --- Scaling --------------------------------------------------------------

/**
 * A round upper bound at or above `max`.
 *
 * Axes that top out at the data maximum put the peak against the frame and give
 * ticks like 4,317. This rounds up to 1, 2, 2.5 or 5 times a power of ten, which
 * is what makes the labels readable.
 */
function niceMax(max: number): number {
  if (max <= 0) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(max));
  const normalized = max / magnitude;

  const step =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export interface Point {
  label: string;
  value: number;
}

// --- Frame ----------------------------------------------------------------

export function ChartFrame({
  title,
  caption,
  action,
  table,
  children,
}: {
  title: string;
  caption?: ReactNode;
  action?: ReactNode;
  /** The same data as a table. Present on every chart, not only on request. */
  table?: ReactNode;
  children: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    // `min-w-0` is load-bearing: a grid item defaults to a content-based
    // minimum width, so without it the scrollable chart inside stretches this
    // card to its own min-width and pushes the whole page sideways.
    <section className="min-w-0 rounded-card border border-hull-700 bg-hull-800/60 p-5 sm:p-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-hull-300">
            {title}
          </h2>
          {caption && <p className="mt-1.5 text-xs leading-relaxed text-hull-400">{caption}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {action}
          {table && (
            <button
              type="button"
              onClick={() => setShowTable((open) => !open)}
              className="rounded-lg border border-hull-600 px-2.5 py-1 text-xs text-hull-300 transition hover:border-surge-400 hover:text-white"
              aria-expanded={showTable}
            >
              {showTable ? 'Hide data' : 'View data'}
            </button>
          )}
        </div>
      </header>

      {children}

      {table && showTable && (
        <div className="mt-5 overflow-x-auto border-t border-hull-700 pt-4">{table}</div>
      )}
    </section>
  );
}

/** Shared table styling, so every chart's data view reads the same. */
export function ChartTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: (string | number)[][];
}) {
  return (
    <table className="w-full min-w-[22rem] text-left text-xs">
      <thead>
        <tr className="text-hull-400">
          {columns.map((column, index) => (
            <th
              key={column}
              scope="col"
              className={cx('pb-2 font-medium', index > 0 && 'text-right')}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="text-hull-200">
        {rows.map((row) => (
          <tr key={String(row[0])} className="border-t border-hull-800">
            {row.map((cell, index) => (
              <td
                key={index}
                className={cx('py-1.5 tabular-nums', index > 0 && 'text-right')}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- Line chart -----------------------------------------------------------

const WIDTH = 760;
const HEIGHT = 260;
const PAD = { top: 16, right: 20, bottom: 32, left: 58 };

/**
 * One series over time, with a crosshair and tooltip.
 *
 * Interactive by default: an SVG chart in a browser that does not answer "what
 * is that point" is withholding data it already has. The hover target is the
 * full column height rather than the mark, so a 2px line is not something the
 * reader has to hit.
 */
export function LineChart({
  points,
  color = SERIES_REVENUE,
  format,
  emptyNote,
}: {
  points: Point[];
  color?: string;
  format: (value: number) => string;
  /** Shown over the axes when every value is zero — an empty chart otherwise
      reads as a flat line at zero, which is a claim rather than an absence. */
  emptyNote?: string;
}) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const max = niceMax(Math.max(...points.map((point) => point.value), 0));
  const allZero = points.every((point) => point.value === 0);

  const x = (index: number) =>
    PAD.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const y = (value: number) => PAD.top + plotHeight - (value / max) * plotHeight;

  const line = points.map((point, index) => `${x(index)},${y(point.value)}`).join(' ');
  const area = `${PAD.left},${PAD.top + plotHeight} ${line} ${x(points.length - 1)},${PAD.top + plotHeight}`;

  /**
   * Axis ticks, with their labels resolved up front.
   *
   * Two things go wrong if the labels are formatted inline. With every value at
   * zero the axis tops out at the smallest representable unit, and money ticks
   * come out as `$0.01, $0.01, $0.01, $0.00, $0` — a scale that is pure noise
   * over data that is simply absent. And on any small range, two adjacent ticks
   * can round to the same string, which reads as a broken axis.
   *
   * So: no numeric labels at all when there is nothing to scale, and otherwise
   * a label is dropped when it would repeat the one below it. The grid line
   * stays either way, because the spacing is still doing work.
   */
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction, index, all) => {
    if (allZero) {
      return { fraction, label: index === 0 ? format(0) : null };
    }
    const label = format(max * fraction);
    const previous = index > 0 ? format(max * (all[index - 1] ?? 0)) : null;
    return { fraction, label: label === previous ? null : label };
  });

  // The peak is worth labelling directly; a number on all twelve points is
  // noise the eye has to filter before it can see the shape.
  const peak = points.reduce(
    (best, point, index) => (point.value > (points[best]?.value ?? 0) ? index : best),
    0,
  );
  const peakPoint = points[peak];
  const hoveredPoint = hover === null ? undefined : points[hover];

  return (
    // The viewBox scales with the container, so at phone width the axis text
    // would render at roughly 5px — present, and unreadable. A floor on the
    // width and a scroller around it keeps the labels legible and lets the
    // reader pan, which is the documented exception for charts too wide to fit.
    <div className="-mx-1 overflow-x-auto px-1">
    <div className="relative min-w-[32rem]">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`Line chart, ${points.length} points, peak ${format(peakPoint?.value ?? 0)}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((tick) => {
          const gridY = PAD.top + plotHeight - tick.fraction * plotHeight;
          return (
            <g key={tick.fraction}>
              <line
                x1={PAD.left}
                y1={gridY}
                x2={WIDTH - PAD.right}
                y2={gridY}
                stroke={GRID}
                strokeWidth="1"
              />
              {tick.label !== null && (
                <text
                  x={PAD.left - 10}
                  y={gridY + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={AXIS_INK}
                >
                  {tick.label}
                </text>
              )}
            </g>
          );
        })}

        {!allZero && (
          <>
            <polygon points={area} fill={`url(#${gradientId})`} />
            <polyline
              points={line}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        )}

        {points.map((point, index) => (
          <text
            key={point.label}
            x={x(index)}
            y={HEIGHT - 10}
            textAnchor="middle"
            fontSize="11"
            fill={AXIS_INK}
          >
            {point.label}
          </text>
        ))}

        {!allZero && peakPoint && (
          <circle
            cx={x(peak)}
            cy={y(peakPoint.value)}
            r="4"
            fill={color}
            // A 2px ring in the surface colour keeps the marker readable where
            // it sits on top of the line it belongs to.
            stroke="#141e33"
            strokeWidth="2"
          />
        )}

        {hover !== null && hoveredPoint && !allZero && (
          <g>
            <line
              x1={x(hover)}
              y1={PAD.top}
              x2={x(hover)}
              y2={PAD.top + plotHeight}
              stroke={color}
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.7"
            />
            <circle
              cx={x(hover)}
              cy={y(hoveredPoint.value)}
              r="5"
              fill={color}
              stroke="#141e33"
              strokeWidth="2"
            />
          </g>
        )}
      </svg>

      {/* Hit targets: one full-height column per point, so the pointer never has
          to find a 2px line. */}
      <div className="absolute inset-0 flex" onMouseLeave={() => setHover(null)}>
        {points.map((point, index) => (
          <div
            key={point.label}
            className="h-full flex-1"
            onMouseEnter={() => setHover(index)}
            onFocus={() => setHover(index)}
            aria-hidden="true"
          />
        ))}
      </div>

      {hover !== null && hoveredPoint && (
        <div
          className="pointer-events-none absolute top-2 rounded-lg border border-hull-600 bg-hull-950/95 px-3 py-2 text-xs shadow-lg"
          style={{
            left: `${(x(hover) / WIDTH) * 100}%`,
            // Flip before the tooltip would run off the right edge.
            transform: hover > points.length / 2 ? 'translateX(-105%)' : 'translateX(5%)',
          }}
          role="status"
        >
          <p className="font-medium text-white">{hoveredPoint.label}</p>
          <p className="mt-0.5 tabular-nums text-hull-300">{format(hoveredPoint.value)}</p>
        </div>
      )}

      {allZero && emptyNote && (
        <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-6 text-center text-sm text-hull-400">
          {emptyNote}
        </p>
      )}
    </div>
    </div>
  );
}

// --- Plan distribution ----------------------------------------------------

export interface PlanSlice {
  label: string;
  value: number;
}

/**
 * Horizontal bars for plan occupancy.
 *
 * Bars rather than a second donut: comparing four magnitudes is what a bar
 * length does well and what an angle does badly. The donut beside it answers
 * "what share", which is the other question.
 */
export function PlanBars({ slices }: { slices: PlanSlice[] }) {
  const max = Math.max(...slices.map((slice) => slice.value), 1);

  return (
    <ul className="space-y-3">
      {slices.map((slice, index) => (
        <li key={slice.label}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="text-hull-200">{slice.label}</span>
            <span className="tabular-nums font-medium text-white">{slice.value}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-hull-900">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.max((slice.value / max) * 100, slice.value > 0 ? 2 : 0)}%`,
                background: PLAN_RAMP[index % PLAN_RAMP.length],
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Share of accounts by plan.
 *
 * Every segment is directly labelled and the legend lists all four, so identity
 * never depends on telling two ramp steps apart. Segments are separated by a
 * 2px gap in the surface colour rather than butted together.
 */
export function PlanDonut({ slices }: { slices: PlanSlice[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const hovered = hover === null ? undefined : slices[hover];

  const size = 180;
  const radius = 70;
  const stroke = 22;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="relative shrink-0">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          role="img"
          aria-label={`Donut chart of ${total} accounts across ${slices.length} plans`}
          className="max-w-full -rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={GRID}
            strokeWidth={stroke}
          />
          {total > 0 &&
            slices.map((slice, index) => {
              const fraction = slice.value / total;
              const length = fraction * circumference;
              // 2px of surface between neighbours, so two adjacent ramp steps
              // never read as one wider segment.
              const gap = slice.value > 0 ? 2 : 0;
              const dash = `${Math.max(length - gap, 0)} ${circumference - Math.max(length - gap, 0)}`;
              const element = (
                <circle
                  key={slice.label}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={PLAN_RAMP[index % PLAN_RAMP.length]}
                  strokeWidth={hover === index ? stroke + 4 : stroke}
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                  className="transition-all"
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                />
              );
              offset += length;
              return element;
            })}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-white">
            {hovered ? hovered.value : total}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-hull-400">
            {hovered ? hovered.label : 'accounts'}
          </span>
        </div>
      </div>

      <ul className="min-w-[11rem] flex-1 space-y-2">
        {slices.map((slice, index) => (
          <li key={slice.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2.5">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: PLAN_RAMP[index % PLAN_RAMP.length] }}
              />
              <span className="truncate text-hull-200">{slice.label}</span>
            </span>
            <span className="shrink-0 tabular-nums text-white">
              {slice.value}
              <span className="ml-1.5 text-xs text-hull-400">
                {total > 0 ? `${Math.round((slice.value / total) * 100)}%` : '—'}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Status list ----------------------------------------------------------

export interface StatusEntry {
  label: string;
  value: number;
  tone: keyof typeof STATUS_COLORS;
  hint?: string;
}

/**
 * Subscription states.
 *
 * A list rather than a chart: four counts with reserved meanings are read one at
 * a time, and plotting them would imply a comparison nobody makes. The swatch is
 * decoration — the label carries the meaning, as a reserved status colour must.
 */
export function StatusList({ entries }: { entries: StatusEntry[] }) {
  return (
    <ul className="space-y-3">
      {entries.map((entry) => (
        <li key={entry.label} className="flex items-start justify-between gap-4">
          <span className="flex min-w-0 items-start gap-2.5">
            <span
              aria-hidden="true"
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: STATUS_COLORS[entry.tone] }}
            />
            <span className="min-w-0">
              <span className="block text-sm text-hull-200">{entry.label}</span>
              {entry.hint && (
                <span className="mt-0.5 block text-xs leading-relaxed text-hull-400">
                  {entry.hint}
                </span>
              )}
            </span>
          </span>
          <span className="shrink-0 text-lg font-semibold tabular-nums text-white">
            {entry.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
